import {
  ErrorSeverity,
  ErrorType,
  OnboardingErrorStep,
} from '@/models/analytics/features/errors';
import { useOnboardingFieldTracking } from '@/models/analytics/features/useOnboardingFieldTracking';
import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyH2,
  TypographyP,
  TypographyP2,
} from '@heidi/design-system/typography';
import {
  AudioBars,
  FALLBACK_SAMPLE_TEXT,
  MicTestReadyState,
  OnboardingStepShell,
  PlaybackCard,
  SIMULATED_TRANSCRIPTION_TOTAL_MS,
  transcriptDialogueWords,
  useMicStream,
  useSimulatedTranscription,
} from '@heidi/growth';
import { FieldType } from '@heidi/runtime/analytics-vocabulary';
import { useTranslations } from '@heidi/runtime/i18n';
import { ArrowRight, Clock, Mic, Play, Square } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * "Play a sample conversation" mic-test step — the control / flag-off variant.
 * Kept verbatim from the previous production design so the auto-fill redesign
 * (`MicTestStep`) can be rolled out behind `release-onboarding-mic-test-autofill`
 * without losing the current experience. See `useIsMicTestAutofillEnabled`.
 */
interface MicTestStepProps {
  onContinue: () => void;
  onSkip: () => void;
  onTranscriptChange: (transcript: string) => void;
  onRecordingDurationChange?: (seconds: number) => void;
  /** Short specialty-specific script to read aloud. Falls back to a generic sample. */
  sampleText?: string;
  /**
   * Gate the Continue button until the user has produced a transcript (via the
   * sample-conversation playback or their own voice). Set to `false` once the
   * user has completed the mic-test once so revisits (e.g. via Back navigation)
   * don't re-block them.
   */
  requireRecordingToContinue?: boolean;
}

type MicState = 'idle' | 'recording' | 'playing' | 'done';

// The sample plays back over a FIXED total duration regardless of script
// length, so every specialty's sample transcribes in the same ~10s. The
// per-word cadence is derived from this (total ÷ word count).
const PLAYBACK_TOTAL_SEC = SIMULATED_TRANSCRIPTION_TOTAL_MS / 1000;

export function MicTestStepPlaySample({
  onContinue,
  onSkip,
  onTranscriptChange,
  onRecordingDurationChange,
  sampleText,
  requireRecordingToContinue = true,
}: MicTestStepProps) {
  const { t } = useTranslations();
  const { trackOnce, trackError } = useOnboardingFieldTracking('mic_test');
  const [micState, setMicState] = useState<MicState>('idle');
  const [revealedWords, setRevealedWords] = useState(0);
  const recordingStartRef = useRef<number | null>(null);

  const sampleWords = useMemo(() => {
    const rawText =
      sampleText && sampleText.trim().length > 0 ? sampleText : FALLBACK_SAMPLE_TEXT;
    // Label-free dialogue words, aligned with the preview's turn render.
    return transcriptDialogueWords(rawText);
  }, [sampleText]);

  const mic = useMicStream();
  const { stopMonitoring } = mic;
  const isRecording = micState === 'recording';

  const handleTranscriptChange = useCallback(
    (text: string) => {
      const count = text.split(' ').filter(Boolean).length;
      setRevealedWords(count);
      onTranscriptChange(text);
    },
    [onTranscriptChange]
  );

  const sim = useSimulatedTranscription(
    mic.isSpeaking,
    isRecording,
    handleTranscriptChange,
    sampleWords,
    { totalDurationMs: SIMULATED_TRANSCRIPTION_TOTAL_MS }
  );

  const reportDuration = useCallback(() => {
    if (recordingStartRef.current !== null) {
      const elapsed = Math.round((Date.now() - recordingStartRef.current) / 1000);
      onRecordingDurationChange?.(elapsed);
      recordingStartRef.current = null;
    }
  }, [onRecordingDurationChange]);

  // Both the live-recording and the no-mic playback paths complete once the
  // whole sample has been transcribed.
  useEffect(() => {
    if (
      (micState === 'recording' || micState === 'playing') &&
      revealedWords >= sampleWords.length
    ) {
      setMicState('done');
      stopMonitoring();
      reportDuration();
    }
  }, [micState, revealedWords, sampleWords.length, stopMonitoring, reportDuration]);

  // No-mic playback: stream the sample consult into the preview word-by-word so
  // the user watches Heidi transcribe a real conversation without granting mic
  // access. Driving `transcript` reuses the existing read-along + generating
  // animation in MicTestPreview.
  useEffect(() => {
    if (micState !== 'playing') return;
    // Scale the cadence so the whole sample streams in over the shared duration,
    // independent of how many words the specialty's sample has.
    const intervalMs = sampleWords.length
      ? SIMULATED_TRANSCRIPTION_TOTAL_MS / sampleWords.length
      : SIMULATED_TRANSCRIPTION_TOTAL_MS;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      handleTranscriptChange(sampleWords.slice(0, i).join(' '));
      if (i >= sampleWords.length) clearInterval(id);
    }, intervalMs);
    return () => clearInterval(id);
  }, [micState, sampleWords, handleTranscriptChange]);

  const handlePlaySample = useCallback(() => {
    // No getUserMedia on this path — the sample plays back locally.
    trackOnce('sample_conversation_play', FieldType.Button);
    setRevealedWords(0);
    recordingStartRef.current = Date.now();
    setMicState('playing');
  }, [trackOnce]);

  const handleStart = useCallback(async () => {
    const granted = await mic.requestPermission();
    if (!granted) {
      // Browser-level permission prompt was dismissed / denied. The button
      // disables itself for the remainder of the session — that's a visible
      // dead-end for the user, so track it as a CRITICAL error.
      trackError({
        step: OnboardingErrorStep.MIC_TEST,
        errorType: ErrorType.PERMISSION_DENIED,
        errorMessage: 'Microphone permission denied or dismissed',
        severity: ErrorSeverity.CRITICAL,
      });
      return;
    }
    // Mic setup is a one-time engagement signal — the user can re-record,
    // but we only want a single `field_selected` event for "they actually
    // started the mic test".
    trackOnce('microphone_setup', FieldType.Button);
    sim.reset();
    setRevealedWords(0);
    recordingStartRef.current = Date.now();
    setMicState('recording');
    mic.startMonitoring();
  }, [mic, sim, trackOnce, trackError]);

  const handleStop = () => {
    setMicState('done');
    stopMonitoring();
    reportDuration();
  };

  // Drives the playback progress bar + time-remaining indicator. Total duration
  // is fixed, so remaining time is the un-revealed fraction of the sample
  // scaled to that window (counts down from ~10s to 0).
  const playbackFractionDone = sampleWords.length
    ? revealedWords / sampleWords.length
    : 0;
  const playbackRemainingSec = Math.max(
    0,
    Math.ceil((1 - playbackFractionDone) * PLAYBACK_TOTAL_SEC)
  );
  const playbackProgressPct = Math.min(100, Math.round(playbackFractionDone * 100));

  if (micState === 'done') {
    return (
      <MicTestReadyState
        title={t('signup.micTestReadyTitle')}
        subtitle={t('signup.micTestReadySubtitle')}
        ctaLabel={t('signup.micTestDoneCta')}
        onContinue={onContinue}
      />
    );
  }

  return (
    <OnboardingStepShell
      footer={
        <div className="flex w-full flex-col gap-2">
          <Button
            size="lg"
            onClick={onContinue}
            disabled={requireRecordingToContinue && revealedWords === 0}
            className="w-full"
          >
            {t('common.continue')}
          </Button>
          <Button
            size="lg"
            type="button"
            variant="ghost"
            onClick={onSkip}
            className="w-full"
          >
            {t('signup.skip')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <TypographyH2>{t('signup.micTestTitle')}</TypographyH2>
        <TypographyP className="text-text-secondary">
          {t('signup.micTestSubtitleSample')}
        </TypographyP>
      </div>

      {/* Primary action — play a sample conversation (no mic). The transcript
          streams into the preview pane on the right, then turns into a note. */}
      <div className="mt-8">
        {isRecording ? (
          <button
            type="button"
            onClick={handleStop}
            className="flex w-full items-center gap-4 rounded-2xl border border-validation-destructive/30 bg-validation-destructive/5 p-5 text-left transition-colors hover:bg-validation-destructive/10"
          >
            <div className="flex size-15 shrink-0 items-center justify-center rounded-full bg-validation-destructive text-text-primary-invert shadow-[0_8px_20px_-8px_rgba(0,0,0,0.45)]">
              <Square className="size-5" />
            </div>
            <div className="flex-1">
              <TypographyP2 className="text-base font-medium text-text-primary">
                {t('signup.micTestListening')}
              </TypographyP2>
            </div>
            <AudioBars level={mic.level} />
          </button>
        ) : micState === 'playing' ? (
          <PlaybackCard
            title={t('signup.micTestPlaying')}
            timeRemaining={t('signup.micTestPlayingTimeRemaining', {
              seconds: playbackRemainingSec,
            })}
            remainingSeconds={playbackRemainingSec}
            progressPct={playbackProgressPct}
          />
        ) : (
          <button
            type="button"
            data-testid="mic-test-button"
            onClick={handlePlaySample}
            className="flex w-full items-center gap-4 rounded-2xl border border-accent-forest/15 bg-accent-forest/[0.05] p-5 text-left transition-colors hover:border-accent-forest/40 hover:bg-accent-forest/[0.08]"
          >
            <div className="flex size-15 shrink-0 items-center justify-center rounded-full bg-accent-forest text-text-primary-invert shadow-[0_8px_20px_-8px_rgba(0,0,0,0.45)]">
              <Play className="size-6 fill-current" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <TypographyP2 className="text-base font-medium text-text-primary">
                  {t('signup.micTestPlayTitle')}
                </TypographyP2>
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-accent-forest/10 px-2 py-0.5 text-xs font-medium text-accent-forest">
                  <Clock className="size-3" />
                  {t('signup.micTestPlayDuration', { seconds: PLAYBACK_TOTAL_SEC })}
                </span>
              </div>
              <TypographyCaption className="mt-0.5 text-text-tertiary">
                {t('signup.micTestPlayHelper')}
              </TypographyCaption>
            </div>
            <ArrowRight className="size-5 shrink-0 text-accent-forest/60 rtl:-scale-x-100" />
          </button>
        )}
      </div>

      {/* Secondary action — record with your own voice (asks for mic access).
          Hidden once the user is recording or playing back. */}
      {micState === 'idle' && (
        <>
          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <TypographyCaption className="text-text-tertiary">
              {t('signup.micTestOr')}
            </TypographyCaption>
            <div className="h-px flex-1 bg-border" />
          </div>

          <button
            type="button"
            onClick={() => void handleStart()}
            disabled={mic.permission === 'denied'}
            className="flex w-full items-center gap-4 rounded-2xl border border-border bg-background-secondary p-5 text-left transition-colors hover:border-accent-forest/30 hover:bg-background-tertiary disabled:cursor-not-allowed disabled:opacity-50"
          >
            <div className="flex size-15 shrink-0 items-center justify-center rounded-full bg-background-tertiary text-text-secondary">
              <Mic className="size-6" />
            </div>
            <div className="flex-1">
              <TypographyP2 className="text-base font-medium text-text-primary">
                {t('signup.micTestUseOwnVoice')}
              </TypographyP2>
              <TypographyCaption className="mt-0.5 text-text-tertiary">
                {t('signup.micTestUseOwnVoiceHelper')}
              </TypographyCaption>
            </div>
            <ArrowRight className="size-5 shrink-0 text-text-tertiary rtl:-scale-x-100" />
          </button>
        </>
      )}
    </OnboardingStepShell>
  );
}
